import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";

import Enrol from "./components/Enrol";
import PairLanding from "./components/PairLanding";
import { api, getToken, setToken } from "./lib/api";
import type { Device, StreamEvent } from "./lib/types";
import { useStream } from "./lib/useStream";
import Feed from "./pages/Feed";
import Lanes from "./pages/Lanes";
import Rejects from "./pages/Rejects";
import System from "./pages/System";
import Watches from "./pages/Watches";

export default function App() {
  const [device, setDevice] = useState<Device | null>(null);
  const [checked, setChecked] = useState(false);

  const verify = async () => {
    if (!getToken()) {
      setChecked(true);
      return;
    }
    try {
      setDevice(await api.me());
    } catch {
      setToken(null);
      setDevice(null);
    } finally {
      setChecked(true);
    }
  };

  useEffect(() => {
    void verify();
  }, []);

  // /pair is reachable without a token on purpose: it is where a phone camera lands after
  // scanning the QR, and that phone has no token yet -- that is the entire point of pairing.
  // Checked before the enrolment gate so it never asks for the bootstrap token instead.
  if (location.pathname === "/pair") return <PairLanding />;
  if (!checked) return null;
  if (!device) return <Enrol onEnrolled={() => void verify()} />;
  return <Shell device={device} />;
}

function Shell({ device }: { device: Device }) {
  const [event, setEvent] = useState<StreamEvent | null>(null);
  const [unseen, setUnseen] = useState(0);
  const status = useStream((next) => {
    setEvent(next);
    if (next.type === "match") setUnseen((n) => n + 1);
  });

  return (
    <div className="shell">
      <header className="masthead">
        <div className="eyebrow">Listing Trawler</div>
        <h1>{titleFor(location.pathname)}</h1>
        <div className="spacer" />
        <span className="row" style={{ gap: 6 }}>
          <span className={`status-dot ${status}`} />
          <span className="faint">{status}</span>
        </span>
        <span className="faint">{device.name}</span>
      </header>

      <nav className="tabs">
        <Tab to="/feed" label={unseen ? `Feed (${unseen})` : "Feed"} onClick={() => setUnseen(0)} />
        <Tab to="/watches" label="Watches" />
        <Tab to="/rejects" label="Rejects" />
        <Tab to="/lanes" label="Lanes" />
        <Tab to="/system" label="System" />
      </nav>

      <Routes>
        <Route path="/" element={<Navigate to="/feed" replace />} />
        <Route path="/feed" element={<Feed event={event} />} />
        <Route path="/watches" element={<Watches />} />
        <Route path="/rejects" element={<Rejects />} />
        <Route path="/lanes" element={<Lanes />} />
        <Route path="/system" element={<System event={event} />} />
        <Route path="*" element={<Navigate to="/feed" replace />} />
      </Routes>
    </div>
  );
}

function Tab({ to, label, onClick }: { to: string; label: string; onClick?: () => void }) {
  return (
    <NavLink to={to} onClick={onClick} className={({ isActive }) => (isActive ? "active" : "")}>
      {label}
    </NavLink>
  );
}

function titleFor(path: string): string {
  if (path.startsWith("/watches")) return "Watches";
  if (path.startsWith("/rejects")) return "Reject log";
  if (path.startsWith("/lanes")) return "Poll lanes";
  if (path.startsWith("/system")) return "System";
  return "Match feed";
}
